import { useMemo, useState } from "react";
import { Button, Stack, Typography, Alert } from "@mui/material";
import Add from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SaveIcon from "@mui/icons-material/Save";
import { useFormik, FormikProvider, FieldArray } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { ConditionalField } from "./ConditionalField";
import { transformFieldsToNestedStructure } from "./utils/transformRequiredFields";
import {
  consumerConditionalFieldsTemplate,
  commercialConditionalFieldsTemplate,
} from "./utils/getConditionalFieldsTemplate";
import { useUpdateLenderArrayFieldsMutation } from "../../../../../store/slices/apiSlice";
import { logicalOperators } from "../../../../../constants";

const ConditionalFields = ({ lenderId, lenderType, requiredFieldsList, initialConditionalFields }) => {
  const hasInitialConditionalFields =
    initialConditionalFields && Array.isArray(initialConditionalFields) && initialConditionalFields.length > 0;
  const [canUseFieldsTemplate, setCanUseFieldsTemplate] = useState(
    !hasInitialConditionalFields && (lenderType === "consumer" || lenderType === "commercial"),
  );
  const [updateLenderArrayFields] = useUpdateLenderArrayFieldsMutation();
  // Sectional conditional fields.
  const transformedRequiredFields = useMemo(
    () => transformFieldsToNestedStructure(requiredFieldsList),
    [requiredFieldsList],
  );
  const sectionList = Object.keys(transformedRequiredFields);

  // Validation schema for conditional fields.
  const validationSchema = Yup.object({
    conditionalFields: Yup.array()
      .of(
        Yup.object({
          section: Yup.string().required("Section is required."),
          subSection: Yup.string().required("Sub section is required."),
          targetField: Yup.string().required("Target field is required."),
          conditionType: Yup.string().when("conditions", {
            is: (conditions) => (conditions ?? []).length > 0,
            then: Yup.string().required("Condition type is required."),
            otherwise: Yup.string().notRequired(),
          }),
          conditions: Yup.array()
            .of(
              Yup.object({
                rule: Yup.string().required("Rule is required."),
                ruleValue: Yup.string().when("rule", {
                  is: (rule) => rule !== "is_empty" && rule !== "is_not_empty",
                  then: Yup.string().required("Rule value is required."),
                  otherwise: Yup.string().notRequired(),
                }),
                section: Yup.string().required("Section is required."),
                subSection: Yup.string().required("Sub section is required."),
                sourceField: Yup.string().required("Source field is required."),
              }),
            )
            .notRequired(),
          validationMessage: Yup.string().notRequired(),
          validations: Yup.array().of(
            Yup.object({
              rule: Yup.string().required("Rule is required."),
              ruleValue: Yup.string().when("rule", {
                is: (rule) => rule !== "is_empty" && rule !== "is_not_empty",
                then: Yup.string().required("Rule value is required."),
                otherwise: Yup.string().notRequired(),
              }),
            }),
          ),
        }),
      ),
  });

  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormik({
    initialValues: {
      conditionalFields: initialConditionalFields,
    },
    onSubmit: async (values) => {
      await updateLenderArrayFields({ lenderId, conditionalFields: values.conditionalFields }).then((res) => {
        if (res?.data && !res.error) {
          enqueueSnackbar("Conditional fields saved successfully", {
            variant: "success",
            autoHideDuration: 5000,
          });
        } else {
          enqueueSnackbar("Failed to save conditional fields", {
            variant: "error",
            autoHideDuration: 5000,
          });
        }
      });
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
  });

  // Use fields template for consumer and commercial lender.
  const handleUseFieldsTemplate = () => {
    if (lenderType === "consumer") {
      formik.setValues({
        conditionalFields: consumerConditionalFieldsTemplate,
      });
    }
    if (lenderType === "commercial") {
      formik.setValues({
        conditionalFields: commercialConditionalFieldsTemplate,
      });
    }
    setCanUseFieldsTemplate(false);
  };

  return (
    <FormikProvider value={formik}>
      <Stack width="100%" rowGap={4} alignItems="flex-start" component="form" onSubmit={formik.handleSubmit}>
        {formik.values.conditionalFields.length === 0 && (
          <Stack width="100%" rowGap={2}>
            {!!formik.errors.conditionalFields && typeof formik.errors.conditionalFields === "string" && (
              <Alert severity="error" onClose={() => formik.setErrors({})}>
                {formik.errors.conditionalFields}
              </Alert>
            )}
            <Typography variant="body1">🤔 It seems like you don't have any fields added yet.</Typography>
          </Stack>
        )}
        <FieldArray name="conditionalFields">
          {({ push, remove }) => {
            return (
              <>
                {formik.values.conditionalFields.map((_field, index) => {
                  return (
                    <ConditionalField
                      key={index}
                      requiredFieldsList={transformedRequiredFields}
                      sectionList={sectionList}
                      index={index}
                      deleteField={() => remove(index)}
                      isLast={index === formik.values.conditionalFields.length - 1}
                    />
                  );
                })}
                <Stack flexDirection="row" justifyContent="space-between" width="100%">
                  <Stack flexDirection="row" justifyContent="flex-start" gap={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => {
                        formik.setErrors({});
                        push({
                          section: sectionList[0],
                          subSection: "self",
                          targetField: undefined,
                          conditionType: logicalOperators[0],
                          conditions: [],
                          hasValidation: false,
                          validationType: logicalOperators[0],
                          validations: [],
                        });
                      }}
                    >
                      Add Field
                    </Button>
                    {canUseFieldsTemplate && (
                      <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={handleUseFieldsTemplate}>
                        Use Fields Template
                      </Button>
                    )}
                  </Stack>
                  <Button variant="contained" startIcon={<SaveIcon />} type="submit">
                    Save Fields
                  </Button>
                </Stack>
              </>
            );
          }}
        </FieldArray>
      </Stack>
    </FormikProvider>
  );
};

export { ConditionalFields };
